<template>
    <DForm
        :errors="errors"
        class="w-full flex flex-col items-center"
        @submit="submit"
    >
        <template v-if="!loginUrl || isNative">
            <DField
                id="school_code"
                label="School Code"
            >
                <DInput
                    v-model="schoolCode"
                    data-cy="school_code"
                    autocorrect="off"
                    autocapitalize="on"
                />
            </DField>

            <DButton
                :loading="loading"
                data-cy="launch"
                size="lg"
                wide
                @click="launch"
            >
                Login
            </DButton>
        </template>
        <template v-else>
            <div
                class="self-start w-[38px] h-[38px] flex items-center justify-center rounded-full cursor-pointer"
                @click="back"
            >
                <PhIcon
                    v-if="isIos"
                    icon="caret-left"
                    weight="light"
                    :size="24"
                />
                <PhIcon
                    v-else
                    icon="arrow-left"
                    weight="light"
                    :size="24"
                />
            </div>
            <DField
                id="session_id"
                label="ASP.NET_SessionId Cookie"
            >
                <DInput
                    v-model="sessionId"
                    data-cy="session_id"
                    autocorrect="off"
                />
            </DField>
            <DField
                id="session_secure_a"
                label="SessionSecureA Cookie"
            >
                <DInput
                    v-model="sessionSecureA"
                    data-cy="session_secure_a"
                    autocorrect="off"
                />
            </DField>
            <DField
                id="session_secure_b"
                label="SessionSecureB Cookie"
            >
                <DInput
                    v-model="sessionSecureB"
                    data-cy="session_secure_b"
                    autocorrect="off"
                />
            </DField>

            <DButton
                :loading="loading"
                data-cy="submit"
                size="lg"
                wide
                @click="submit"
            >
                Submit
            </DButton>
        </template>
    </DForm>
</template>

<script setup>
    import {
        DButton, DInput, DField, DForm,
    } from '@digistorm/spark'
    import { Capacitor } from '@capacitor/core'
    import { usePlatformAuthStore } from '@/stores/platformAuth'
    import { usePlatformAuthFireflyStore } from '@/stores/platformAuthFirefly'
    import { useDeviceStore } from '@/stores'
    import PhIcon from '@/components/app/PhIcon.vue'

    const emit = defineEmits(['loading'])

    const props = defineProps({
        authRequirements: Object,
        loading: Boolean,
    })

    const deviceStore = useDeviceStore()
    const platformAuthStore = usePlatformAuthStore()
    const platformAuthFireflyStore = usePlatformAuthFireflyStore()
    const { snackbar } = useSnackbars()

    const { isIos } = storeToRefs(deviceStore)

    const errors = ref(null)
    const loginUrl = ref(null)

    const schoolCode = ref('')

    const sessionId = ref('')
    const sessionSecureA = ref('')
    const sessionSecureB = ref('')

    const isNative = computed(() => Capacitor.isNativePlatform())

    const clearDetails = () => {
        sessionId.value = ''
        sessionSecureA.value = ''
        sessionSecureB.value = ''
    }

    const back = () => {
        if (props.loading) {
            return
        }
        loginUrl.value = null
        clearDetails()
    }

    const submit = async () => {
        if (props.loading || !loginUrl.value) {
            return
        }

        emit('loading', true)
        platformAuthFireflyStore.login({
            url: loginUrl.value,
            schoolCode: schoolCode.value,
            sessionId: sessionId.value,
            sessionSecureA: sessionSecureA.value,
            sessionSecureB: sessionSecureB.value,
        })
            .then(() => {
                clearDetails()
            })
    }

    const launch = () => {
        if (props.loading) {
            return
        }

        if (isNative.value && !Capacitor.isPluginAvailable('InAppBrowser')) {
            snackbar({
                message: 'Firefly login requires a newer app build',
                type: 'error',
            })
            return
        }

        emit('loading', true)
        platformAuthFireflyStore.start({
            url: props.authRequirements.url,
            schoolCode: schoolCode.value,
        })
            .then((data) => {
                if (!isNative.value) {
                    loginUrl.value = data.login_url
                    emit('loading', false)
                }
            })
    }

    const handleError = (e) => {
        if (get(e, 'response.status') === 422 && get(e, 'response.data.errors')) {
            errors.value = get(e, 'response.data.errors')
        }
    }

    onMounted(() => {
        platformAuthStore.events.on('error', handleError)
    })

    onUnmounted(() => {
        platformAuthStore.events.off('error', handleError)
    })
</script>
